'use client';

import './RecruitForm.css';

import { useRef, useState } from 'react';
import { Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';

import { businessInfo } from '@/config/config';


export default function RecruitForm() {
    const formRef = useRef<HTMLFormElement>(null);
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
    const [agreed, setAgreed] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('sending');

        const form = formRef.current;
        if (!form) return;

        const formData = new FormData(form);

        const res = await fetch('/api/contact', {
            method: 'POST',
            body: formData
        });

        if (res.ok) {
            setStatus('sent');
            form.reset();
            setAgreed(false);
        } else {
            setStatus('error');
        }
    };

    return (
        <>
            <div className="recruit-form w-100">
                <Form onSubmit={handleSubmit} ref={formRef}>
                    <Form.Group controlId="name" className="mb-3">
                        <Form.Label>Nimi *</Form.Label>
                        <Form.Control type="text" name="name" required />
                    </Form.Group>

                    <Form.Group controlId="email" className="mb-3">
                        <Form.Label>Sähköposti *</Form.Label>
                        <Form.Control type="email" name="email" required />
                    </Form.Group>

                    <Form.Group controlId="message" className="mb-3">
                        <Form.Label>Lisätietoja</Form.Label>
                        <Form.Control as="textarea" name="message" rows={5} />
                    </Form.Group>

                    <Form.Group controlId="agreement" className="mb-3">
                        <Form.Check
                            type="checkbox"
                            name="agreement"
                            required
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            label={
                                <>
                                    Olen lukenut ja hyväksyn{' '}
                                    <a href="#" onClick={(e) => { e.preventDefault(); setShowModal(true); }}>
                                        tietosuojaselosteen
                                    </a>
                                    .
                                </>
                            }
                        />
                    </Form.Group>


                    <Button
                        className='custom-button primary'
                        type="submit"
                        disabled={status === 'sending' || !agreed}
                    >
                        {status === 'sending' ? (
                            <>
                                <Spinner size="sm" animation="border" /> Sending…
                            </>
                        ) : (
                            'Send'
                        )}
                    </Button>

                    {status === 'sent' && <Alert variant="success" className="mt-3">✅ Lähetetty onnistuneesti!</Alert>}
                    {status === 'error' && <Alert variant="danger" className="mt-3">❌ Lomakkeen lähetys epäonnistui. Yritä uudelleen.</Alert>}
                </Form>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Tietosuojaseloste – Tukiluotsi</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Tämä lomake kerää henkilötietoja Tukiluotsin rekrytointitarkoituksiin. Käsittelemme
                        henkilötietojasi luottamuksellisesti ja ainoastaan ilmoitettuihin tarkoituksiin.
                    </p>

                    <p>
                        <strong>1. Rekisterinpitäjä</strong>
                    </p>
                    <p>
                        Tukiluotsi<br />
                        {/* Replace with your actual company address if public */}
                        Osoite: {businessInfo.address}<br />
                        Sähköposti: <a href={`mailto:${businessInfo.email}`}>{businessInfo.email}</a>
                    </p>

                    <p>
                        <strong>2. Henkilötietojen käsittelyn tarkoitus ja oikeusperuste</strong>
                    </p>
                    <p>
                        Keräämme henkilötietoja saapuneiden työhakemusten vastaanottamiseen, arviointiin ja
                        mahdolliseen yhteydenottoon hakijan kanssa.
                    </p>
                    <p>
                        Henkilötietojen käsittely perustuu <strong>suostumukseesi</strong>, jonka annat
                        lähettämällä lomakkeen. Sinulla on oikeus peruuttaa suostumuksesi milloin tahansa
                        ottamalla meihin yhteyttä yllä mainittuun sähköpostiosoitteeseen.
                    </p>

                    <p>
                        <strong>3. Kerättävät henkilötiedot</strong>
                    </p>
                    <ul>
                        <li>Nimi</li>
                        <li>Sähköpostiosoite</li>
                        <li>Viesti (vapaaehtoinen, sisältäen työhakemuksen tiedot)</li>
                    </ul>

                    <p>
                        <strong>4. Tietojen säilytysaika</strong>
                    </p>
                    <p>
                        Henkilötiedot säilytetään 30 päivän ajan lomakkeen lähettämisestä. Tämän jälkeen tiedot
                        poistetaan järjestelmästämme automaattisesti. Tämä säilytysaika on määritelty
                        rekrytointiprosessin tarpeisiin ja hakijoiden yhdenvertaisen kohtelun varmistamiseksi.
                    </p>

                    <p>
                        <strong>5. Tietojen vastaanottajat ja käsittelijät</strong>
                    </p>
                    <p>
                        Henkilötietoihisi on pääsy ainoastaan Tukiluotsin rekrytoinnista vastaavilla henkilöillä,
                        jotka tarvitsevat tietoja työtehtäviensä suorittamiseen.
                    </p>
                    <p>
                        Käytämme <strong>Resend-palvelua</strong> lomakkeella lähetettyjen viestien
                        sähköpostivälitykseen. Resend toimii henkilötietojen käsittelijänä ja on sitoutunut
                        noudattamaan EU:n yleistä tietosuoja-asetusta (GDPR).
                    </p>

                    <p>
                        <strong>6. Tietojen siirto EU:n tai ETA-alueen ulkopuolelle</strong>
                    </p>
                    <p>
                        Käyttäessämme Resend-palvelua, henkilötietoja saatetaan siirtää Euroopan unionin tai
                        Euroopan talousalueen ulkopuolelle (esim. Yhdysvaltoihin), jossa Resendillä on palvelimia.
                        Varmistamme tietojen asianmukaisen suojan käyttämällä tiedonsiirron oikeusperusteena
                        Euroopan komission hyväksymiä <strong>vakiolausekkeita</strong> ja muita tarvittavia
                        lisäsuojatoimia GDPR:n vaatimusten mukaisesti.
                    </p>

                    <p>
                        <strong>7. Rekisteröidyn oikeudet</strong>
                    </p>
                    <p>Sinulla on henkilötietojesi käsittelyyn liittyen seuraavat oikeudet:</p>
                    <ul>
                        <li>
                            <strong>Oikeus saada pääsy tietoihisi:</strong> Voit pyytää vahvistuksen siitä,
                            käsittelemmekö henkilötietojasi, ja pyytää pääsyn omiin tietoihisi.
                        </li>
                        <li>
                            <strong>Oikeus tietojen oikaisemiseen:</strong> Voit vaatia virheellisten tai
                            epätarkkojen tietojen oikaisua.
                        </li>
                        <li>
                            <strong>Oikeus tietojen poistamiseen eli oikeus tulla unohdetuksi:</strong> Voit vaatia
                            tietojesi poistamista tietyissä tilanteissa, esimerkiksi jos tietojasi ei enää tarvita
                            siihen tarkoitukseen, jota varten ne kerättiin.
                        </li>
                        <li>
                            <strong>Oikeus käsittelyn rajoittamiseen:</strong> Voit vaatia tietojesi käsittelyn
                            rajoittamista tietyissä tilanteissa, esim. jos kiistät tietojen paikkansapitävyyden.
                        </li>
                        <li>
                            <strong>Oikeus vastustaa käsittelyä:</strong> Voit vastustaa tietojesi käsittelyä
                            tietyissä tilanteissa, esimerkiksi suoramarkkinointitarkoituksiin.
                        </li>
                        <li>
                            <strong>Oikeus siirtää tiedot järjestelmästä toiseen:</strong> Voit saada itse
                            antamasi henkilötiedot jäsennellyssä ja yleisesti käytetyssä muodossa ja siirtää ne
                            toiselle rekisterinpitäjälle.
                        </li>
                    </ul>
                    <p>
                        Voit käyttää näitä oikeuksia ottamalla meihin yhteyttä sähköpostitse osoitteeseen{' '}
                        <a href={`mailto:${businessInfo.email}`}>{businessInfo.email}</a>. Käsittelemme
                        pyyntösi viivytyksettä ja vastaamme siihen kuukauden kuluessa pyynnön vastaanottamisesta.
                    </p>

                    <p>
                        <strong>8. Oikeus tehdä valitus valvontaviranomaiselle</strong>
                    </p>
                    <p>
                        Sinulla on oikeus tehdä valitus toimivaltaiselle valvontaviranomaiselle, jos katsot, että
                        henkilötietojesi käsittelyssä rikotaan soveltuvaa tietosuojalainsäädäntöä. Suomessa
                        valvontaviranomainen on <strong>Tietosuojavaltuutetun toimisto</strong>.
                    </p>
                    <p>
                        Yhteystiedot:<br />
                        Tietosuojavaltuutetun toimisto<br />
                        Lintulahdenkuja 4<br />
                        00530 HELSINKI<br />
                        tietosuoja.fi
                    </p>

                    <p>
                        <strong>9. Tietoturva</strong>
                    </p>
                    <p>
                        Käsittelemme tietojasi luottamuksellisesti ja suojaamme niitä asianmukaisin teknisin ja
                        organisatorisin toimenpitein luvattomalta pääsyltä, muuttamiselta, luovuttamiselta tai
                        tuhoutumiselta.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Sulje
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}